import { useState } from "react";
import type { Branch, UpdateBranch } from "@dentacore/shared-types";
import { parseExchangeRate } from "@dentacore/shared-types";

import { PhoneInput } from "../ui/phone-input";
import { CountryPicker, CurrencyPicker, TimezonePicker } from "../ui/pickers";
import styles from "./settings.module.css";
import { RequiredMark } from "../ui/required-mark";

/**
 * **What a Google Maps link looks like, checked only well enough to catch a
 * paste that plainly is not one** (2026-09-20).
 *
 * The real check is the server's: whether the link names a position takes a
 * redirect to resolve and a parse to read, and a regular expression in the
 * browser cannot do either. This exists so that pasting an address, a phone
 * number or a Facebook link is answered while the admin is still looking at
 * the field, rather than by a round trip.
 *
 * **The field replaced a latitude and a longitude** the admin had to find
 * themselves. What they are for has not changed: the WhatsApp reminder's
 * template declares a LOCATION header, and Meta refuses the send unless every
 * message carries a pin beside the branch's name and address. The server reads
 * the pair out of this link.
 */
const MAPS_LINK = /^https:\/\/(?:[a-z0-9-]+\.)*(?:google\.[a-z.]{2,}|goo\.gl)\//i;

/**
 * One branch's fields — the same form whether it is opening a new location or
 * editing one that exists.
 *
 * One form for both because the fields are identical, and a second nearly
 * identical form is where the two drift. `branch === null` is the create case,
 * and every field starts empty so that anything left alone reaches the server
 * as omitted rather than as a value this application chose. See CLAUDE.md,
 * *Data is configured, not coded*.
 *
 * **Turnover is not here, and that is deliberate** (2026-09-23). It belongs to
 * the chair — a room's clean-down time — and is set in the calendar's *Edit
 * chairs* dialog beside the chair it describes. A location-wide field forced
 * one number onto every chair in the building.
 *
 * **Timezone is the exception, and it is not the same case.** `defaultTimezone`
 * seeds the create form because it is the clinic's *own* stored value, chosen
 * by this customer at sign-up — filling it in is repeating their answer back to
 * them, not inventing one, so it stays inside *data is configured, not coded*
 * where a literal `"Asia/Beirut"` would not. The settings page says as much a
 * section above ("seeds new locations only"); before 2026-09-07 it did not, and
 * the very first thing every new practice did was open a location and find the
 * button dead.
 *
 * **The currency is edited here, never on create** (QA4-D6). A new location
 * starts in the clinic's home currency; changing an existing one asks for the
 * rate — "1 old = X new" — and the save converts the location's prices at it.
 */
export function BranchForm({
  branch,
  busy,
  defaultTimezone = "",
  onCancel,
  onSubmit,
}: {
  branch: Branch | null;
  busy: boolean;
  /** The clinic's own default, for the create case only. */
  defaultTimezone?: string;
  onCancel: () => void;
  onSubmit: (body: UpdateBranch) => void;
}): React.JSX.Element {
  const [name, setName] = useState(branch?.name ?? "");
  const [timezone, setTimezone] = useState(branch?.timezone ?? defaultTimezone);
  const [address, setAddress] = useState(branch?.address ?? "");
  const [country, setCountry] = useState(branch?.country ?? "");
  const [phone, setPhone] = useState(branch?.phoneE164 ?? "");
  const [mapsUrl, setMapsUrl] = useState(branch?.mapsUrl ?? "");
  const [currency, setCurrency] = useState(branch?.currency ?? "");
  const [rate, setRate] = useState("");

  /** A new currency needs the rate its prices convert at, typed exactly. */
  const changingCurrency = branch !== null && currency !== "" && currency !== branch.currency;
  const rateMissing = changingCurrency && parseExchangeRate(rate) === null;

  /**
   * **Empty is fine; wrong is not.** A branch with no location is a supported
   * state — the reminder refuses to send for it and says so — so the only
   * thing refused here is a link that is plainly not Google Maps.
   */
  const linkLooksWrong = mapsUrl.trim() !== "" && !MAPS_LINK.test(mapsUrl.trim());

  const complete = name.trim() !== "" && timezone.trim() !== "" && !linkLooksWrong && !rateMissing;

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        if (!complete) return;

        onSubmit({
          name: name.trim(),
          timezone: timezone.trim(),
          address: address.trim(),
          country: country === "" ? undefined : country,
          // Omitted rather than sent empty: a branch with no number is a
          // supported state, and neither `""` nor a bare dialling code is an
          // E.164 number.
          ...(phone.trim().length < 4 ? {} : { phoneE164: phone.trim() }),
          // **An explicit null rather than an omission**, so emptying the
          // field actually removes a location that was there. Omitting it
          // would mean "leave it alone", which is the opposite.
          mapsUrl: mapsUrl.trim() === "" ? null : mapsUrl.trim(),
          ...(changingCurrency ? { currency, exchangeRate: rate.trim() } : {}),
        });
      }}
    >
      <div className={styles.formGrid}>
        <label className={styles.field}>
          <span className={styles.label}>
            Name
            <RequiredMark />
          </span>
          <input
            className={styles.input}
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Whatever this location is called"
            autoFocus
            data-testid="branch-field-name"
          />
        </label>

        <TimezonePicker
          id="branch-field-timezone"
          label="Timezone"
          required
          value={timezone}
          onChange={setTimezone}
          testId="branch-field-timezone"
        />

        <label className={styles.field}>
          <span className={styles.label}>Address</span>
          <input
            className={styles.input}
            value={address}
            onChange={(event) => setAddress(event.target.value)}
            data-testid="branch-field-address"
          />
        </label>

        {/*
          **Country sits above the phone because it fills it** (decision D4).

          The client asked for the country rather than a dial code — *"instead
          of country code let's have country and the code is auto added to the
          phone fields"* — and this is where it is answered: choosing it here
          prefills this location's own number, and every phone field recorded
          at this location afterwards.
        */}
        <CountryPicker
          id="branch-field-country"
          label="Country"
          value={country}
          onChange={setCountry}
          testId="branch-field-country"
          note="Fills the dialling code on phone numbers recorded at this location."
        />

        <div className={styles.spanTwo}>
          <PhoneInput
            value={phone}
            onChange={setPhone}
            // Picked above, so the code appears the moment the country does —
            // rather than after a save and a reload.
            defaultCountry={country === "" ? null : country}
            testIdPrefix="branch-field-phone"
          />
        </div>

        <label className={`${styles.field} ${styles.spanTwo}`}>
          <span className={styles.label}>Google Maps link</span>
          <input
            className={styles.input}
            value={mapsUrl}
            onChange={(event) => setMapsUrl(event.target.value)}
            placeholder="https://www.google.com/maps/place/..."
            inputMode="url"
            data-testid="branch-field-maps-url"
          />
          <small className={styles.formHint}>
            Open this location on Google Maps and copy the link. It is what the WhatsApp
            reminder&rsquo;s map pin is built from &mdash; without it, reminders for this branch
            cannot be sent.
          </small>
        </label>

        {branch !== null && (
          <CurrencyPicker
            id="branch-field-currency"
            label="Currency"
            required
            value={currency}
            onChange={(next) => {
              setCurrency(next);
              setRate("");
            }}
            testId="branch-field-currency"
            note="What this location's prices are in."
          />
        )}

        {changingCurrency && branch !== null && (
          <label className={styles.field}>
            <span className={styles.label}>
              Exchange rate: 1 {branch.currency} = how many {currency}
              <RequiredMark />
            </span>
            <input
              className={styles.input}
              value={rate}
              onChange={(event) => setRate(event.target.value)}
              inputMode="decimal"
              placeholder={`How many ${currency} one ${branch.currency} is worth`}
              data-testid="branch-field-rate"
            />
          </label>
        )}

        {changingCurrency && branch !== null && (
          <p className={`${styles.formHint} ${styles.spanTwo}`} data-testid="branch-currency-note">
            Saving converts this location&rsquo;s prices from {branch.currency} to {currency} at
            this rate, each rounded to the nearest {currency} minor unit: its procedures and their
            visits, its stock prices, its draft invoices and treatment-plan steps not yet billed.
            Invoices already issued, payments, salaries and expenses keep the currency they were
            recorded in.
          </p>
        )}

        {linkLooksWrong ? (
          <p className={styles.spanTwo} role="alert" data-testid="branch-maps-url-error">
            That does not look like a Google Maps link. It should start with https:// and be a
            google.com/maps address or a maps.app.goo.gl share link.
          </p>
        ) : null}
      </div>

      <div className={styles.formActions}>
        <button type="submit" className={styles.primary} disabled={busy || !complete}>
          {branch === null ? "Open it" : "Save changes"}
        </button>
        {/* A disabled button that says nothing is a dead end: the click does
            nothing and the page does not react, so there is no way to learn
            what is missing. Name it. */}
        {!complete && !busy && (
          <span className={styles.formHint} role="status" data-testid="branch-form-blocked">
            {name.trim() === "" && timezone.trim() === ""
              ? "A name and a timezone are needed."
              : name.trim() === ""
                ? "This location needs a name."
                : timezone.trim() === ""
                  ? "This location needs a timezone."
                  : rateMissing
                    ? "Type the exchange rate the prices convert at."
                    : // The one remaining way a location can be wrong. The field
                      // states the reason itself; this only says which field,
                      // because the button is what stopped reacting.
                      "The Google Maps link needs fixing."}
          </span>
        )}
        <button type="button" className={styles.ghost} disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
